import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import {
  commitSelectionAtom,
  committedCameraMapAtom,
  committedGroupMapAtom,
  removedFromGroupAtom,
} from './state';

function omitKeys<T>(
  prev: Record<string, T>,
  ids: string[]
): Record<string, T> {
  const next = { ...prev };
  ids.forEach((id) => delete next[id]);
  return next;
}

// Owns every mutation of the grid selection — the checkbox, shift-range,
// select-all, drag-and-drop, tile cross and tab close all go through here.
export function useMultiCamSelection() {
  const commit = useSetAtom(commitSelectionAtom);
  const setCommittedCameraMap = useSetAtom(committedCameraMapAtom);
  const setCommittedGroupMap = useSetAtom(committedGroupMapAtom);
  const setRemovedFromGroup = useSetAtom(removedFromGroupAtom);

  const removeCameras = useCallback(
    (cameraIds: string[]) => {
      setCommittedCameraMap((prev) => omitKeys(prev, cameraIds));
    },
    [setCommittedCameraMap]
  );

  const removeGroup = useCallback(
    (cameraGroupId: string) => {
      setCommittedGroupMap((prev) => omitKeys(prev, [cameraGroupId]));
      setRemovedFromGroup((prev) => omitKeys(prev, [cameraGroupId]));
    },
    [setCommittedGroupMap, setRemovedFromGroup]
  );

  const removeCameraFromGroup = useCallback(
    (cameraGroupId: string, cameraId: string) => {
      setRemovedFromGroup((prev) => ({
        ...prev,
        [cameraGroupId]: [...(prev[cameraGroupId] || []), cameraId],
      }));
    },
    [setRemovedFromGroup]
  );

  return { commit, removeCameras, removeGroup, removeCameraFromGroup };
}

export default useMultiCamSelection;
